import type { UseQueryResult } from "@tanstack/react-query";

import type { OperatorsAdminEffectiveResponse } from "../../operators";
import {
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetPanel,
  SheetPopup,
  SheetTitle,
} from "~/components/ui/sheet";

import { SettingsInfoTip } from "./SettingsInfoTip";

function formatRoleLabel(role: string): string {
  return role.replaceAll("_", " ");
}

export function AdminEffectivePermissionsDrawer(props: {
  effectiveQuery: UseQueryResult<OperatorsAdminEffectiveResponse>;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  selectedEmail: string | null;
  selectedName: string | null;
}) {
  const { effectiveQuery, onOpenChange, open, selectedEmail, selectedName } = props;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetPopup side="right" className="w-[min(24rem,calc(100vw-2rem))] max-w-md bg-card p-0">
        <SheetHeader className="border-b border-border/70 px-5 py-4">
          <div className="flex items-center gap-1.5 pe-8">
            <SheetTitle className="text-base font-semibold tracking-tight">
              Effective permissions
            </SheetTitle>
            <SettingsInfoTip label="Exact permissions this user currently has, and which roles grant each one. Super admins bypass these checks." />
          </div>
          <SheetDescription>
            {selectedName && selectedEmail
              ? `${selectedName} · ${selectedEmail}`
              : selectedEmail
                ? `Showing grants for ${selectedEmail}`
                : "Select a user from the table to inspect permission origins."}
          </SheetDescription>
        </SheetHeader>

        <SheetPanel className="px-5 py-4">
          {effectiveQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading permissions…</p>
          ) : effectiveQuery.isError ? (
            <p className="text-sm text-destructive">
              {effectiveQuery.error instanceof Error
                ? effectiveQuery.error.message
                : "Failed to load permissions"}
            </p>
          ) : effectiveQuery.data ? (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Roles: {effectiveQuery.data.roles.map(formatRoleLabel).join(", ") || "none"}
                {effectiveQuery.data.isSuperAdmin ? " · super admin" : ""}
              </p>
              {effectiveQuery.data.permissions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {effectiveQuery.data.isSuperAdmin
                    ? "No explicit grants listed; super admin bypasses checks."
                    : "No permissions granted."}
                </p>
              ) : (
                <ul className="space-y-2">
                  {effectiveQuery.data.permissions.map((permission) => (
                    <li
                      key={permission.key}
                      className="rounded-lg border border-border bg-background px-3 py-2"
                    >
                      <p className="font-mono text-xs text-foreground">{permission.key}</p>
                      <p className="mt-1 text-xs text-muted-foreground">
                        via {permission.roles.map(formatRoleLabel).join(", ")}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No permission data.</p>
          )}
        </SheetPanel>
      </SheetPopup>
    </Sheet>
  );
}
